(ns app.front.view.admin.commands.migrate-cr-trades
  (:require [app.front.ui :as ui]
            [app.front.ui.combo-box :as ui-combo]
            [app.front.ui.select :as ui-select]
            [app.front.ui.table :as ui-table]
            [app.front.view.group.admin.common :as acommon]
            [clojure.string :as str]
            [core.front.admin :as admin]
            [core.front.carbon :as cc]
            [core.front.event :refer [<sub >evt]]
            [core.util.util :as util]
            [re-frame.core-instrumented :as rf]))

;; State
(rf/reg-sub ::state :-> ::state)

(rf/reg-sub ::cr-groups
  :<- [::admin/groups]
  (fn [groups]
    (->> groups
         (filter #(str/ends-with? (:group/id %) "-CR"))
         (sort-by :group/name))))

(rf/reg-sub ::selected-group
  :<- [::state]
  :-> :selected-group)

(rf/reg-sub ::group-id
  :<- [::selected-group]
  :-> :group/id)

(rf/reg-sub ::registrations
  :<- [::state]
  :-> :registrations)

(rf/reg-sub ::targets-by-category
  :<- [::state]
  :-> :targets-by-category)

(rf/reg-sub ::loading?
  :<- [::state]
  :-> :loading?)

(rf/reg-sub ::migrate-modal
  :<- [::state]
  :-> :migrate-modal)

(rf/reg-sub ::target-config-id
  :<- [::state]
  :-> :target-config-id)

(rf/reg-sub ::all-available-targets
  :<- [::targets-by-category]
  (fn [targets-by-category]
    (when targets-by-category
      (->> (vals targets-by-category)
           (apply concat)
           (sort-by (juxt :category-name :work-type-name))))))

;; Events
(rf/reg-event-db ::set-selected-group
  (fn [db [_ group]]
    (-> db
        (assoc-in [::state :selected-group] group)
        (assoc-in [::state :registrations] nil)
        (assoc-in [::state :targets-by-category] nil))))

(rf/reg-event-db ::set-target-config-id
  (fn [db [_ config-id]]
    (assoc-in db [::state :target-config-id] config-id)))

(rf/reg-event-fx ::fetch-registrations
  (fn [{:keys [db]} [_ group-id]]
    {:db           (-> db
                       (assoc-in [::state :loading?] true)
                       (assoc-in [::state :registrations] nil)
                       (assoc-in [::state :targets-by-category] nil))
     :comm/command {:command    :admin/get-registrations-with-removed-trades
                    :method     :post
                    :data       {:group/id group-id}
                    :on-success [::fetch-registrations.success]
                    :on-error   [::fetch-registrations.error]}}))

(rf/reg-event-db ::fetch-registrations.success
  (fn [db [_ _ {:keys [registrations targets-by-category]}]]
    (-> db
        (assoc-in [::state :loading?] false)
        (assoc-in [::state :registrations] registrations)
        (assoc-in [::state :targets-by-category] targets-by-category))))

(rf/reg-event-fx ::fetch-registrations.error
  (fn [{:keys [db]} _]
    {:db       (assoc-in db [::state :loading?] false)
     :dispatch [:notifications/add :toast :error :failure "Failed to fetch registrations"]}))

(rf/reg-event-db ::open-migrate-modal
  (fn [db [_ registration]]
    (assoc-in db [::state :migrate-modal] registration)))

(rf/reg-event-db ::close-migrate-modal
  (fn [db _]
    (-> db
        (update ::state dissoc :migrate-modal)
        (update ::state dissoc :target-config-id))))

(rf/reg-event-fx ::migrate-single-trade
  (fn [{:keys [db]} [_ group-id trade-id target-config-id]]
    {:db           (assoc-in db [::state :loading?] true)
     :comm/command {:command    :admin/migrate-single-registration-trade
                    :method     :post
                    :data       {:trade-id      trade-id
                                 :target-config target-config-id}
                    :on-success [::migrate-single-trade.success group-id]
                    :on-error   [::migrate-single-trade.error]}}))

(rf/reg-event-fx ::migrate-single-trade.success
  (fn [{:keys [db]} [_ group-id _ _]]
    {:db         (assoc-in db [::state :loading?] false)
     :dispatch-n [[::close-migrate-modal]
                  [:notifications/add :toast :success :success "Trade migrated successfully"]
                  [::fetch-registrations group-id]]}))

(rf/reg-event-fx ::migrate-single-trade.error
  (fn [{:keys [db]} _]
    {:db       (assoc-in db [::state :loading?] false)
     :dispatch [:notifications/add :toast :error :failure "Failed to migrate trade"]}))

(rf/reg-event-db ::cleanup
  (fn [db _]
    (dissoc db ::state)))

;; Components
(defn migrate-button [{:keys [trade-id work-type-name registrant-name category-id category-name]}]
  (cc/button {:kind     :tertiary
              :size     :small
              :on-click #(>evt [::open-migrate-modal {:trade-id        trade-id
                                                      :work-type-name  work-type-name
                                                      :registrant-name registrant-name
                                                      :category-id     category-id
                                                      :category-name   category-name}])}
             "Migrate"))

(defn migrate-modal []
  (let [{:keys [trade-id work-type-name registrant-name category-name]} (<sub [::migrate-modal])
        open?                                                           (some? trade-id)]
    (when open?
      (let [group-id          (<sub [::group-id])
            target-config-id  (<sub [::target-config-id])
            available-targets (or (<sub [::all-available-targets]) [])
            loading?          (<sub [::loading?])]
        [cc/modal
         {:open                    true
          :primary-button-text     "Migrate"
          :secondary-button-text   "Cancel"
          :modal-heading           "Migrate trade to new work-type"
          :primary-button-disabled (or loading? (nil? target-config-id))
          :on-request-close        #(>evt [::close-migrate-modal])
          :on-request-submit       #(>evt [::migrate-single-trade group-id trade-id target-config-id])}
         [:div
          [:p.mb-4 "Migrate trade for " [:strong registrant-name] " from "
           [:strong work-type-name] " (" category-name ") to a new work-type configuration."]
          [ui-select/select
           {:id           "target-work-type"
            :label-text   "Target work-type"
            :value        target-config-id
            :values       (map :work-type-id available-targets)
            :val->str     (fn [id]
                            (->> available-targets
                                 (some #(when (= (:work-type-id %) id) %))
                                 ((fn [target]
                                    (str (:category-name target) " - " (:work-type-name target))))))
            :on-change    #(>evt [::set-target-config-id %])
            :allow-empty? true
            :empty-text   "Select target work-type"
            :light        true}]]]))))

(defn registrations-table []
  (let [registrations (<sub [::registrations])
        loading?      (<sub [::loading?])]
    (cond
      loading?
      [cc/inline-loading {:description "Loading..."}]

      (nil? registrations)
      [:p.text-ui-text-02 "Select a group and click Search to find registrations with removed trades."]

      (empty? registrations)
      [:p.text-ui-text-02 "No registrations found with removed trades for this group."]

      :else
      [ui-table/modern-responsive-table
       {:items registrations
        :cols  [["Registrant" :registrant-name]
                ["Category" :category-name]
                ["Removed trade" :work-type-name]
                ["Trade ID" :trade-id {:flex "0 1 120px"}]
                ["" migrate-button {:flex "0 1 100px"}]]}])))

(defn- main-content []
  (let [tr             (<sub [:tr])
        cr-groups      (<sub [::cr-groups])
        selected-group (<sub [::selected-group])
        group-id       (:group/id selected-group)
        loading?       (<sub [::loading?])]
    [:div
     [migrate-modal]
     [:section
      [ui/help
       (str "When a trade (work-type) configuration is deleted and recreated with the same name, "
            "existing contractor registrations may still reference the old (removed) trade. "
            "This tool helps find and migrate those registrations to the new trade configuration.\n\n"
            "Steps:\n"
            "1. Select a contractor registration group from the dropdown\n"
            "2. Click Search to find registrations with removed trades\n"
            "3. For each registration, click 'Migrate' to change it to a new work-type")]

      [:div.flex.items-end.gap-4.mt-4
       [:div {:style {:width "400px"}}
        [ui-combo/combo-box {:id          "cr-group-selector"
                             :tr          tr
                             :items       (or cr-groups [])
                             :item->id    :group/id
                             :value       selected-group
                             :on-change   #(>evt [::set-selected-group %])
                             :placeholder "Select a group"
                             :label-text  "Contractor registration group"
                             :item->str   #(str (:group/name %) " (" (:group/id %) ")")}]]
       (cc/button {:kind     :primary
                   :disabled (or loading? (str/blank? group-id))
                   :size     :field
                   :on-click #(>evt [::fetch-registrations group-id])}
                  "Search")]

      [:div.mt-6
       [registrations-table]]]]))

(defn view [match]
  [acommon/admin-page-v2
   (util/mmap {:title "Migrate contractor registration trades"}
              match)
   [main-content]])

(def route
  {:name        :view/admin.commands.migrate-cr-trades
   :own-layout? true
   :view        view
   :controllers [{:start (fn [_] (>evt [::admin/get-authority-groups]))}
                 {:stop (fn [_] (>evt [::cleanup]))}]})
